// Diagnostics card on /offline-control that reconciles four ping counters so
// the user can see exactly where missing points live: native Swift queue,
// JS outbox (IndexedDB), server-confirmed rows, and lifetime captures observed
// by the JS layer. Pure UI — does not capture, drain, or send anything.

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-shim";
import { RoavaCard } from "@/components/roava/RoavaCard";
import { useNativeQueueStats, tickTrackingReliability } from "@/lib/native/tracking-reliability";
import { useOutboxStats } from "@/lib/native/use-outbox-stats";
import { getPingReconciliation } from "@/lib/trip.functions";
import { useReachability } from "@/lib/connectivity/reachability";
import { Database, CloudUpload, Server, Activity } from "lucide-react";


const CAPTURE_KEY = "roava.captureCounter.lifetime";

function readCaptureCount(): number {
  try {
    if (typeof localStorage === "undefined") return 0;
    const v = Number(localStorage.getItem(CAPTURE_KEY) ?? "0");
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}

function useCaptureCount(): number {
  const [n, setN] = useState<number>(() => readCaptureCount());
  useEffect(() => {
    const tick = () => setN(readCaptureCount());
    // Refresh every 2s while visible; also on drain events so the number is
    // never further behind than one display poll.
    const id = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      tick();
    }, 2_000);
    const onDrain = () => tick();
    if (typeof window !== "undefined") {
      window.addEventListener("mtsync:drain-complete", onDrain);
    }
    return () => {
      clearInterval(id);
      if (typeof window !== "undefined") {
        window.removeEventListener("mtsync:drain-complete", onDrain);
      }
    };
  }, []);
  return n;
}

export function PingReconciliationCard({ tripId }: { tripId: string | null | undefined }) {
  const native = useNativeQueueStats();
  const outbox = useOutboxStats();
  const captured = useCaptureCount();
  const reach = useReachability();

  const reconFn = useServerFn(getPingReconciliation);
  const { data: server } = useQuery({
    queryKey: ["ping-reconciliation", tripId],
    queryFn: () => reconFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
    staleTime: 10_000,
    refetchInterval: 15_000,
  });

  const nativeCount = native?.native_queue_count ?? 0;
  const outboxCount = outbox.queued ?? 0;
  const serverCount = server?.serverCount ?? 0;

  // If anything is sitting in the native buffer (including rejected weak/stale
  // fixes that haven't been cleared yet), kick a reliability tick so the
  // counter doesn't show a "1 waiting to sync" for points that were actually
  // dropped by the quality gate. Single-flight + 5s min gap protects spam.
  useEffect(() => {
    if (nativeCount > 0) void tickTrackingReliability("recon-card-nonzero");
  }, [nativeCount]);

  const onDevice = nativeCount + outboxCount;
  const draining = outbox.draining === true;
  const linkUp = reach.link !== "none" && reach.link !== "unknown";
  const backendOk = reach.backendReachable === true;
  const backendBlocked = linkUp && reach.backendReachable === false;

  // State-aware "why are these waiting" explanation. Plain language only —
  // no "JS outbox", "IndexedDB", "POST", etc.
  let whyWaiting: string | null = null;
  if (onDevice > 0) {
    if (!linkUp) {
      whyWaiting = `No internet right now. ${onDevice.toLocaleString()} point${onDevice === 1 ? " is" : "s are"} safe on this phone and will upload automatically when signal returns.`;
    } else if (backendBlocked) {
      whyWaiting = `You have a connection, but Roava can't reach the server yet (often a captive Wi-Fi page or temporary outage). Points stay safe on this phone until the server responds.`;
    } else if (draining) {
      whyWaiting = `Upload is running now and working through the backlog in batches. Larger backlogs take a few minutes — leaving this screen is fine, sync keeps going.`;
    } else if (backendOk) {
      whyWaiting = `The phone recorded points faster than the link could upload them — common after a long offline stretch. Roava is catching up; tap Sync now to push a batch immediately.`;
    } else {
      whyWaiting = `${onDevice.toLocaleString()} point${onDevice === 1 ? "" : "s"} saved on this phone, waiting to upload.`;
    }
  }

  let verdict: { kind: "syncing" | "missing" | "ok"; text: string };
  if (onDevice > 0) {
    verdict = {
      kind: "syncing",
      text: `~${onDevice.toLocaleString()} point${onDevice === 1 ? "" : "s"} on this device waiting to sync. Tap Sync now.`,
    };
  } else if (captured > 0 && serverCount < captured) {
    const gap = captured - serverCount;
    verdict = {
      kind: "missing",
      text: `${gap.toLocaleString()} point${gap === 1 ? "" : "s"} captured this session weren't accepted — the server already had them (duplicates collapsed by the unique index).`,
    };
  } else {
    verdict = { kind: "ok", text: "All synced." };
  }

  const verdictClass =
    verdict.kind === "syncing"
      ? "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30"
      : verdict.kind === "missing"
      ? "bg-destructive/10 text-destructive border-destructive/30"
      : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30";

  return (
    <RoavaCard>
      <h3 className="text-base font-semibold text-[var(--roava-text)] mb-1">Where your route points are</h3>
      <p className="text-[11px] text-muted-foreground mb-3 leading-snug">
        Points travel: <span className="font-medium">phone buffer → upload queue → server</span>. Each tile shows how many points are at that stage right now.
      </p>
      <div className="grid grid-cols-2 gap-2.5">
        <Cell
          icon={<Database className="size-4" />}
          label="On phone (buffer)"
          value={nativeCount}
          hint="Accepted GPS fixes not yet handed to upload. Weak or stale fixes are dropped, not counted here."
        />

        <Cell
          icon={<CloudUpload className="size-4" />}
          label="Ready to upload"
          value={outboxCount}
          hint="Queued in Roava, waiting to send"
        />
        <Cell
          icon={<Server className="size-4" />}
          label="On the server"
          value={serverCount}
          hint="Confirmed saved in your trip"
        />
        <Cell
          icon={<Activity className="size-4" />}
          label="Captured this session"
          value={captured}
          hint="Total GPS fixes seen since app start"
        />
      </div>
      <div className={`mt-3 px-3 py-2 rounded-md border text-xs leading-snug ${verdictClass}`}>
        {verdict.text}
      </div>
      {whyWaiting && (
        <div className="mt-2 px-3 py-2 rounded-md border border-[var(--roava-border)] bg-background/60 text-[11px] leading-snug text-muted-foreground">
          <span className="font-semibold text-[var(--roava-text)]">Why are these waiting? </span>
          {whyWaiting}
        </div>
      )}
    </RoavaCard>
  );
}


function Cell({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  hint: string;
}) {
  return (
    <div className="rounded-lg border bg-background/60 p-2.5">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-1 text-xl font-semibold tabular-nums text-[var(--roava-text)]">
        {value.toLocaleString()}
      </div>
      <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>
    </div>
  );
}
